<template>
  <div class="slider-module" :id="module.id">
    <div
      class="module-slider-container"
      :style="[modulePadding, sliderElStyles]"
      :class="'template-' + templateName"
    >
      <GlobalUIMedia
        v-if="module.config.general.showMedia"
        :media="module.config.general.media"
        :lazy="lazy"
      />

      <div class="slider-wrapper">
        <div ref="slider" class="keen-slider">
          <component
            :is="slide.component.substring(2)"
            v-for="slide in module.modules"
            :key="slide.id"
            :module="slide"
            :parent="module"
            :is-nested-module="true"
            :lazy="lazy"
            @emitted-click="$emit('emittedClick', { module, child: slide })"
          />

          <button
            v-if="slider && showArrows"
            :class="{
              arrow: true,
              'arrow--left': true,
              'arrow--disabled': disablePrevArrow,
            }"
            aria-label="Previous"
            @click="slider.prev()"
            data-testid="arrow-left"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 37 37"
              fill="none"
            >
              <g opacity=".75">
                <circle cx="18.319" cy="18.834" r="18" fill="#fff" />
                <path
                  fill="#000"
                  d="M11.157 18.303a.75.75 0 0 0 0 1.06l4.773 4.774a.75.75 0 0 0 1.061-1.06l-4.243-4.244 4.243-4.242a.75.75 0 0 0-1.06-1.06l-4.774 4.772Zm13.794-.22H11.688v1.5H24.95v-1.5Z"
                />
              </g>
            </svg>
          </button>

          <button
            v-if="slider && showArrows"
            :class="{
              arrow: true,
              'arrow--right': true,
              'arrow--disabled': disableNextArrow,
            }"
            aria-label="Next"
            @click="slider.next()"
            data-testid="arrow-right"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 36 37"
            >
              <g opacity=".75">
                <circle
                  cx="18"
                  cy="18"
                  r="18"
                  fill="#fff"
                  transform="matrix(-1 0 0 1 36 .833)"
                />
                <path
                  fill="#000"
                  d="M25.162 18.303a.75.75 0 0 1 0 1.06l-4.773 4.774a.75.75 0 1 1-1.06-1.06l4.242-4.244-4.243-4.242a.75.75 0 0 1 1.061-1.06l4.773 4.772Zm-13.794-.22h13.264v1.5H11.368v-1.5Z"
                />
              </g>
            </svg>
          </button>
        </div>

        <div
          v-if="slider && module.config.general.showDots"
          class="dots"
          :style="
            '--slider-dot-active-color:' +
            module.config.general.activeDotColor.value
          "
        >
          <button
            v-for="(slide, idx) in dotHelper"
            :key="idx"
            :class="{ dot: true, '-active': current === idx }"
            :aria-label="'Go to slide ' + (idx + 1)"
            @click="slider.moveToIdx(idx)"
          ></button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { globalComputed, globalMethods } from "../GlobalUI/mixins/global.js";
import sliderModule from "./sliderDefaultModuleProp.js";

import "keen-slider/keen-slider.min.css";
import KeenSlider from "keen-slider";
import GlobalUIMedia from "../GlobalUI/GlobalUIMedia.vue";
import ImageSlide from "./Slide/ImageSlide.vue";
import VideoSlide from "./Slide/VideoSlide.vue";

export default {
  name: "SliderModule",
  components: { ImageSlide, VideoSlide, GlobalUIMedia },
  mixins: [globalComputed, globalMethods],
  props: {
    module: {
      type: Object,
      default() {
        return sliderModule;
      },
    },
    lazy: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["emittedClick", "slide-visible"],
  data() {
    return {
      slider: null,
      current: 0,
      slideObservers: [],
      visibleSlides: [],
    };
  },
  computed: {
    dotHelper() {
      return this.slider
        ? [...Array(this.slider.track.details.slides.length).keys()]
        : [];
    },
    isLastSlide() {
      return (
        this.slider &&
        this.current === this.slider.track.details.slides.length - 1
      );
    },
    sliderElStyles() {
      const { moduleBackgroundColor } = this.module.config.general;

      const styles = {
        backgroundColor: moduleBackgroundColor.value,
      };

      return styles;
    },
    templateName() {
      return this.module.config.general.template.component || "sixteenToNine";
    },
    loopSlides() {
      // Loop only if enabled and more than one slide
      return (
        this.module.config.general.loopSlides && this.module.modules.length > 1
      );
    },
    showArrows() {
      // Only show arrows if more than one slide
      return this.dotHelper.length > 1;
    },
    disablePrevArrow() {
      // Disable only if not looping and at the first slide
      return !this.loopSlides && this.current === 0;
    },
    disableNextArrow() {
      // Disable only if not looping and at the last slide
      return !this.loopSlides && this.isLastSlide;
    },
  },
  watch: {
    "module.modules": {
      handler() {
        this.$nextTick(() => {
          this.initializeSlider();
        });
      },
      deep: true,
    },
    loopSlides: {
      handler() {
        this.$nextTick(() => {
          this.initializeSlider();
        });
      },
    },
  },
  mounted() {
    this.initializeSlider();
  },
  beforeUnmount() {
    if (this.slider) this.slider.destroy();
    // Disconnect all observers when component is unmounted
    this.disconnectObservers();
  },

  methods: {
    initializeSlider() {
      // Destroy previous slider instance if it exists
      if (this.slider) {
        this.slider.destroy();
        this.slider = null;
      }

      // Disconnect any existing observers
      this.disconnectObservers();

      // Make sure the slider element exists
      if (!this.$refs.slider) return;

      this.slider = new KeenSlider(this.$refs.slider, {
        mode: "free-snap",
        slides: {
          origin: "center",
          perView: "auto",
          spacing: 32,
        },
        dragSpeed: 0.5,
        initial: this.current,
        loop: this.loopSlides,
        slideChanged: (s) => {
          this.current = s.track.details.rel;

          // Get slides only from this slider instance
          let totalSlides = Array.from(
            this.$refs.slider.querySelectorAll(".keen-slider__slide"),
          );
          totalSlides.forEach((s) => {
            s.classList.remove("-active");
          });
          // Add safety check to prevent errors if current slide doesn't exist
          if (totalSlides[this.current]) {
            totalSlides[this.current].classList.add("-active");
          }
        },
      });

      // Initialize the first slide as active, scoped to this slider instance
      this.$refs.slider
        .querySelector(".keen-slider__slide")
        ?.classList?.add("-active");

      // Setup Intersection Observer for slides after slider is initialized
      this.$nextTick(() => {
        this.setupSlideObservers();
      });
    },

    setupSlideObservers() {
      // First disconnect any existing observers
      this.disconnectObservers();
      this.visibleSlides = [];

      // Get all slides in this slider
      const slides = Array.from(
        this.$refs.slider.querySelectorAll(".keen-slider__slide"),
      );

      const options = {
        root: null,
        rootMargin: "0px",
        threshold: 0.5,
      };

      slides.forEach((slide, index) => {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              // Slide is now visible
              if (!this.visibleSlides.includes(index)) {
                this.visibleSlides.push(index);
                this.onSlideVisible(index, slide);
              }
            } else {
              // Remove from visible slides array when no longer visible
              const visibleIndex = this.visibleSlides.indexOf(index);
              if (visibleIndex !== -1) {
                this.visibleSlides.splice(visibleIndex, 1);
              }
            }
          });
        }, options);

        // Start observing the slide
        observer.observe(slide);

        // Store the observer for cleanup
        this.slideObservers.push(observer);
      });
    },

    disconnectObservers() {
      // Disconnect all observers
      this.slideObservers.forEach((observer) => {
        observer.disconnect();
      });
      this.slideObservers = [];
    },

    onSlideVisible(index, slideElement) {
      this.$emit("slide-visible", {
        index,
        slideElement,
        child: this.module.modules[index],
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@include reset;

.slider-module {
  container-type: inline-size;
  contain: layout;
}

.module-slider-container {
  position: relative;
  width: 100%;
  margin: auto;
  padding-inline: 0;

  @include laptop {
    padding-inline: 0;
  }

  @include mobile {
    padding-inline: 0;
  }
}

.slider-wrapper {
  position: relative;
  display: flex;
  flex-direction: column;
}

.dots {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 16px 0;
}

.dot {
  cursor: pointer;

  width: 10px;
  height: 10px;
  margin: 0 5px;

  background: #c5c5c5;
  border: none;
  border-radius: 50%;
}

.dot.-active {
  width: 14px;
  height: 14px;
  background-color: var(--slider-dot-active-color, #000);
}

.arrow {
  @include reset-button;

  cursor: pointer;

  position: absolute;
  top: 50%;
  transform: translateY(-50%);

  width: 36px;
  height: 100%;

  @include mobile {
    width: 24px;
  }

  .template-SixteenToNine & {
    top: 250px;

    @include tablet {
      top: 168px;
    }

    @include mobile {
      top: calc((100cqw - 65px) / (16 / 9) / 2);
    }
  }

  .template-FourToThree & {
    top: 250px;

    @include mobile {
      top: calc((100cqw - 65px) / (4 / 3) / 2);
    }
  }

  .template-OneToOne & {
    top: 250px;

    @include mobile {
      top: calc((100cqw - 65px) / 2);
    }
  }

  .template-NineToSixteen & {
    top: 311px;

    @include mobile {
      top: calc((100cqw - 65px) / (9 / 16) / 2);
    }
  }
}

.arrow--left {
  left: 5px;
}

.arrow--right {
  right: 5px;
  left: auto;
}

.arrow--disabled {
  display: none;
}
</style>
